import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";

const StatisticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialDate = searchParams.get('date')
    ? new Date(searchParams.get('date'))
    : new Date();

  const [currentMonth, setCurrentMonth] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(initialDate.toISOString().split('T')[0]);
  const [monthData, setMonthData] = useState({});
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarExpanded, setCalendarExpanded] = useState(false);

  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Get all dates in the month grid (including padding from prev/next months)
  const getMonthGrid = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    let startPadding = firstDay.getDay() - 1;
    if (startPadding < 0) startPadding = 6;

    const days = [];

    // Previous month padding
    for (let i = startPadding - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }

    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true });
    }

    // Next month padding to complete the grid
    const remaining = 42 - days.length; // 6 rows x 7 days
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }

    return days;
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  useEffect(() => {
    const fetchMonthData = async () => {
      if (!user) return;
      setLoading(true);

      try {
        // Fetch all challenges
        const challengesRef = collection(db, `users/${user.uid}/challenges`);
        const challengesSnapshot = await getDocs(challengesRef);

        const challengesList = challengesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setChallenges(challengesList);

        // Aggregate progress data for all challenges
        const progressData = {};

        await Promise.all(
          challengesSnapshot.docs.map(async (challengeDoc) => {
            const progressRef = collection(db, `users/${user.uid}/challenges/${challengeDoc.id}/progress`);
            const progressSnapshot = await getDocs(progressRef);

            progressSnapshot.docs.forEach((progressDoc) => {
              const dateStr = progressDoc.id;
              const data = progressDoc.data();

              if (!progressData[dateStr]) {
                progressData[dateStr] = { total: 0, challenges: {} };
              }
              progressData[dateStr].total += data.total || 0;
              progressData[dateStr].challenges[challengeDoc.id] = {
                name: challengeDoc.data().originalName,
                total: data.total || 0,
                goal: challengeDoc.data().goal || 0
              };
            });
          })
        );

        setMonthData(progressData);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthData();
  }, [user]);

  const getTotalGoal = () => {
    return challenges.reduce((sum, c) => sum + (c.goal || 0), 0);
  };

  const getSelectedDayData = () => {
    return monthData[selectedDate] || { total: 0, challenges: {} };
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDateKey(date) === formatDateKey(today);
  };

  const monthGrid = getMonthGrid();
  const selectedDayData = getSelectedDayData();
  const totalGoal = getTotalGoal();

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Back button */}
      <button
        onClick={() => navigate("/")}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </button>

      {/* Selected Day Details - Now at top */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          {new Date(selectedDate).toLocaleDateString('en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
        </h3>

        {loading ? (
          <div className="space-y-3 animate-pulse">
            <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg" />
            <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg" />
          </div>
        ) : challenges.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No challenges yet</p>
        ) : (
          <div className="space-y-3">
            {challenges.map(challenge => {
              const challengeData = selectedDayData.challenges[challenge.id];
              const total = challengeData?.total || 0;
              const goal = challenge.goal || 0;
              const percentage = goal > 0 ? Math.min((total / goal) * 100, 100) : 0;
              const unit = challenge.goalType === 'minutes' ? 'min' : '';

              return (
                <div key={challenge.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {challenge.originalName}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {total}{unit}/{goal}{unit}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          percentage >= 100 ? 'bg-green-500' : 'bg-indigo-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <button
                      onClick={() => navigate(`/register?challenge=${challenge.id}&date=${selectedDate}`)}
                      className="flex-shrink-0 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium text-sm transition-colors"
                      title="Add entry"
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Calendar - Now below with expand/collapse */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
        {/* Header with expand toggle */}
        <button
          onClick={() => setCalendarExpanded(!calendarExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-medium text-gray-900 dark:text-white">
              {calendarExpanded ? 'Calendar' : 'View Calendar'}
            </span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${calendarExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Expandable calendar content */}
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${calendarExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-6 pt-2 border-t border-gray-100 dark:border-gray-700">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {daysOfWeek.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            {loading ? (
              <div className="grid grid-cols-7 gap-1">
                {[...Array(42)].map((_, i) => (
                  <div key={i} className="aspect-square p-1">
                    <div className="w-full h-full bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {monthGrid.map(({ date, isCurrentMonth }, index) => {
                  const dateKey = formatDateKey(date);
                  const dayData = monthData[dateKey];
                  const hasProgress = dayData && dayData.total > 0;
                  const percentage = hasProgress && totalGoal > 0
                    ? Math.min((dayData.total / totalGoal) * 100, 100)
                    : 0;
                  const isSelected = dateKey === selectedDate;
                  const isTodayDate = isToday(date);

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedDate(dateKey)}
                      className={`aspect-square p-1 rounded-lg transition-all ${
                        isSelected
                          ? 'ring-2 ring-indigo-500'
                          : ''
                      }`}
                    >
                      <div className={`w-full h-full rounded-lg flex flex-col items-center justify-center relative ${
                        !isCurrentMonth
                          ? 'text-gray-300 dark:text-gray-600'
                          : hasProgress
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                            : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                      } ${isTodayDate ? 'ring-2 ring-indigo-400 ring-inset' : ''}`}>
                        <span className={`text-sm font-medium ${!isCurrentMonth ? 'opacity-40' : ''}`}>
                          {date.getDate()}
                        </span>
                        {hasProgress && isCurrentMonth && (
                          <div className="w-6 h-1 bg-gray-200 dark:bg-gray-600 rounded-full mt-1 overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;
